"use client";

import { useState } from "react";
import PropTypes from "prop-types";

import { mapRoomLabel } from "src/helpers/mapRoomLabel";
import { Ga4WithEventParameters } from "src/helpers/google-tag-manager/events/ga4-with-eventparameters";
import { getColumns } from "@/src/helpers/project-radar/table-columns";

import Button from "src/components/common/button";
import ResponsiveContainer from "src/components/grid/responsive-container";
import Table from "src/components/project-radar-landing-page/table";
import PaginationWrapper from "src/components/pagination-wrapper";
import ProjectRadarSVG from "src/assets/svg/components/projectRadar/ProjectRadarSVG";
import ContactFormModal from "src/components/project-detail/contact-form-modal";
import SelectedFilterArea from "src/components/listing-search/selected-filter-area";
import ProjectCard from "./card";
import ProjectRadarFilter from "../filter";
import ModalFilter from "../filter/modalFilter";
import NoResults from "./noResults";

import styles from "./styles.module.css";
import { getPaginationArray } from "src/helpers/pagination-helper";

const ProjectRadarContent = ({ projectCards, currentPage, maximumPage, filters, searchCriteria, baseUrl }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [currentCompany, setCurrentCompany] = useState({});
  const [formData, setFormData] = useState();

  const listingCount = 10;
  const keys = maximumPage ? [...Array(maximumPage).keys()] : [];

  const onclickGtmEvent = (review) => {
    const gtmLabel = {
      project_name: review?.review?.slug,
      project_company_name: review?.review?.company?.slug,
      city: review?.review?.locationInfo?.cityName,
      town: review?.review?.locationInfo?.districtName,
      oda_sayisi: review?.roomType,
      ilan_fiyat: review?.unitPrice,
      ilan_metrekare: review?.meterSquarePrice,
      pesinat: review?.cash,
      faiz: review?.interest,
      vade: review?.maturity,
    };
    setTimeout(() => {
      Ga4WithEventParameters(
        {
          eventName: "proje_radari_incele",
          eventParameters: gtmLabel,
        },
        300
      );
    });
  };

  const columns = getColumns(styles, onclickGtmEvent);

  const listingPaginationItems = getPaginationArray(keys, currentPage, maximumPage) || [];

  const handleContactModal = (project) => {
    setIsOpen(true);
    setCurrentCompany(project?.company?.id);

    const { slug: projectName, id: projectId, company, locationInfo } = project;
    const { slug: projectCompanyName, id: companyId } = company || {};
    const { cityName: city, cityId, districtName: town, districtId: townId } = locationInfo || {};

    setFormData({
      project: { projectName, projectId },
      company: { projectCompanyName, companyId },
      city: { city, cityId },
      town: { town, townId },
    });

    setTimeout(() => {
      Ga4WithEventParameters({
        eventName: "proje_radari_bilgi_click",
        eventParameters: {
          project_name: projectName,
          project_company_name: projectCompanyName,
          city,
          town,
        },
      });
    }, 300);
  };

  const handleProjectTable = (project) => {
    const { campaigns, url: projectUrl } = project;
    const campaignTableDatas = {
      cash: campaigns.find((campaign) => campaign.name === "CASH")?.campaignTitle,
      interest: campaigns.find((campaign) => campaign.name === "INTEREST")?.campaignTitle,
      maturity: campaigns.find((campaign) => campaign.name === "DELAY")?.campaignTitle,
      review: { ...project },
    };

    const tableData = project?.unitTypes.map((unit) => {
      return {
        ...campaignTableDatas,
        roomType: mapRoomLabel(unit?.roomType),
        infoMeters: `${unit?.unitArea}  m²`,
        price: { value: unit?.unitPrice, currency: unit?.currency },
        meterSquarePrice: { value: unit?.meterSquarePrice?.toString().split(".")[0], currency: unit?.currency },
        review: {
          ...campaignTableDatas,
          hrefUrl: `${projectUrl}#?unite-tipi-${unit?.unitArea}-${unit?.roomType}`,
          roomType: mapRoomLabel(unit?.roomType),
          unitPrice: unit?.unitPrice,
          meterSquarePrice: unit?.meterSquarePrice?.toString().split(".")[0],
        },
      };
    });
    return <Table columns={columns} data={tableData} />;
  };

  return (
    <ResponsiveContainer>
      <div className={styles.titleWrapper}>
        <h2 className={styles.title}>Proje Radarı'ndan Ödeme Avantajlı Sıfır Konut Projeleri</h2>
        <p className={styles.descriptionText}>
          Ödeme kolaylığı sağlayan{" "}
          <span className={styles.boldDescriptionText}>fiyat detaylı sıfır konut projeleri</span> Emlakjet’te!{" "}
          <span className={styles.boldDescriptionText}>Proje Radarı</span> ile firmaların sizlere sunacağı özel
          teklifleri inceleyebilir, karşılaştırmalar yapabilir ve size en uygun proje için bizimle iletişime
          geçebilirsiniz.{" "}
        </p>
        <p className={styles.descriptionText}>
          Daha fazla bilgi edinmek için form doldurabilirsiniz! En kısa sürede size geri dönüş sağlayacağız.
        </p>
      </div>
      <ModalFilter filters={filters} criteria={searchCriteria} />
      <div className={styles.selectedFilterArea}>
        <SelectedFilterArea criteria={searchCriteria} isProjectRadar />
      </div>
      <div className={styles.filterAndCards}>
        <div className={styles.filters}>
          <ProjectRadarFilter filters={filters} criteria={searchCriteria} />
        </div>
        {projectCards?.total > 0 ? (
          <div>
            {projectCards?.records.map((project, index) => (
              <div className={styles.wrapper} key={index}>
                <div className={styles.cardAndTable}>
                  <ProjectCard project={project} index={index} />
                  {handleProjectTable(project)} {/* TABLE COMPONENT */}
                  <Button buttonClass={styles.button} onClick={() => handleContactModal(project)}>
                    Detaylı Bilgi
                  </Button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <NoResults />
        )}
      </div>
      <PaginationWrapper
        totalListingLength={projectCards?.total}
        total={listingPaginationItems}
        current={currentPage}
        showFirstItem={currentPage >= 4}
        showLastItem={currentPage + 4 <= maximumPage && keys.length > 5}
        maximumPage={maximumPage}
        listingCount={listingCount}
        url={baseUrl}
      />
      <ContactFormModal
        isOpen={isOpen}
        handleClose={() => setIsOpen(false)}
        currentCompany={currentCompany}
        icon={<ProjectRadarSVG />}
        isResponsive
        isProjectRadar
        project={formData}
      />
    </ResponsiveContainer>
  );
};

ProjectRadarContent.propTypes = {
  projectCards: PropTypes.object,
  currentPage: PropTypes.number,
  maximumPage: PropTypes.number,
  filters: PropTypes.object,
  searchCriteria: PropTypes.object,
  baseUrl: PropTypes.object,
};

export default ProjectRadarContent;
